import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/exam :: ExamCountdownTimer
 *
 * Pinned countdown timer for an in-flight exam session. Re-renders every
 * second using a `setInterval` + `Date.now()` comparison against the
 * server-provided deadlineMs (we deliberately avoid decrement-a-counter
 * because that drifts if the tab is backgrounded).
 *
 * Contract with ExamSession (Hard Rule #6 — "Real DMV doesn't wait"):
 *
 *   1. onTimeout fires EXACTLY ONCE when remaining <= 0 — cleared
 *      interval + fired flag so re-renders never spam the callback.
 *   2. Interval is cleared on unmount.
 *   3. aria-live region announces at minute granularity only (we don't
 *      want a screen reader to read "59 seconds remaining" every tick).
 *
 * Colour thresholds — visual hint, not the accessibility story:
 *
 *   > 60s   → text-brand-primary   (calm)
 *   ≤ 60s   → text-brand-accent    (warning)
 *   ≤ 10s   → text-danger          (urgent)
 *
 * Hard Rule #7 (color-blind-safe) isn't violated here because the timer
 * is numeric — the digits themselves convey urgency, the tint just
 * reinforces it.
 */
export interface ExamCountdownTimerProps {
  /** Server-supplied absolute deadline in ms since epoch. */
  deadlineMs: number;
  onTimeout: () => void;
}

function formatMMSS(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1_000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function toneFor(remainingMs: number): string {
  if (remainingMs <= 10_000) return "text-danger";
  if (remainingMs <= 60_000) return "text-brand-accent";
  return "text-brand-primary";
}

export default function ExamCountdownTimer({
  deadlineMs,
  onTimeout,
}: ExamCountdownTimerProps) {
  const { t } = useTranslation();
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, deadlineMs - Date.now()),
  );
  const firedRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    // Reset when deadline changes (shouldn't happen mid-session, but we
    // want to behave sanely if the parent re-starts).
    firedRef.current = false;
    setRemainingMs(Math.max(0, deadlineMs - Date.now()));

    const tick = () => {
      const next = Math.max(0, deadlineMs - Date.now());
      setRemainingMs(next);
      if (next <= 0 && !firedRef.current) {
        firedRef.current = true;
        onTimeoutRef.current();
      }
    };
    const id = setInterval(tick, 250);
    // Prime in case deadline already passed.
    tick();
    return () => {
      clearInterval(id);
    };
  }, [deadlineMs]);

  const tone = toneFor(remainingMs);
  const label = formatMMSS(remainingMs);
  const minutesLeft = Math.max(0, Math.ceil(remainingMs / 60_000));

  return (
    <div
      className={`inline-flex items-center gap-xs rounded-md border border-brand-primary/10 bg-white px-sm py-xs font-mono text-lg font-semibold ${tone}`}
      data-testid="exam-countdown-timer"
      aria-label={t("exam.timer.ariaLabel", { time: label })}
    >
      <span aria-hidden="true">{label}</span>
      {/* Screen-reader announcement — minute granularity to avoid spam. */}
      <span
        className="sr-only"
        data-testid="exam-countdown-live"
        aria-live="polite"
      >
        {t("exam.timer.srAnnouncement", { minutes: minutesLeft })}
      </span>
    </div>
  );
}
